import React from 'react';
import { Target } from 'lucide-react';
import { useAds } from '../hooks/useAds';
import { budgetTone, connectionStatus, costText, costVsGoal, money, tokenWarning } from '../utils/ads';
import { ago } from '../utils/botQuestions';
import AdCampaigns from '../components/AdCampaigns';

const card = 'bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-3';

function Fact({ label, value, note, tone }) {
  return (
    <div className="bg-seryi-bg-dark rounded-xl p-3">
      <dd className="text-sm font-extrabold text-seryi-heading">{value}</dd>
      <dt className="text-[10px] text-seryi-text">{label}</dt>
      {note && <p className={`text-[10px] mt-1 ${tone || 'text-seryi-text'}`}>{note}</p>}
    </div>
  );
}

export default function AdsPage() {
  const { summary, loading, missing } = useAds();

  if (missing) {
    return <div className="p-6 text-xs text-amber-300">Falta ejecutar la migración <code>20261025a_ads_base.sql</code> en Supabase para usar la pantalla de anuncios.</div>;
  }
  const conn = summary?.connection || { status: 'sin_conectar' };
  const st = connectionStatus(conn.status);
  const warning = tokenWarning(conn.token_expires_at);
  const month = summary?.month;
  const period = summary?.period;
  const limits = summary?.limits;
  const goal = costVsGoal(period?.cost_per_conversation, limits?.goal_cost_per_conversation);
  const campaigns = summary?.campaigns || [];

  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <h2 className="text-xl font-extrabold text-seryi-heading flex items-center gap-2"><Target size={20} /> Anuncios en Meta</h2>
        <p className="text-xs text-seryi-text mt-1">Cuánto gastas en anuncios de Facebook e Instagram que llevan a tu WhatsApp, cuántas conversaciones te traen y cuánto cuesta cada una. Nada se gasta sin tu aprobación y hay un tope de gasto por día y por mes.</p>
      </div>

      {loading && <p className="text-xs text-seryi-text">Cargando…</p>}

      <section className={card} aria-label="Conexión con Meta">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-black text-seryi-heading">Conexión con Facebook e Instagram</h3>
          <span className={`text-[11px] font-bold ${st.tone}`}>{st.label}</span>
        </div>
        {conn.status === 'sin_conectar' ? (
          <>
            <p className="text-xs text-seryi-text">Todavía no hay una cuenta conectada. La conexión se activa cuando Meta apruebe los permisos de la app de Seryiads; apenas esté lista, aquí podrás conectar tu página y tu cuenta publicitaria y las cifras aparecerán solas.</p>
            <button type="button" disabled className="bg-slate-700 text-slate-400 text-xs font-extrabold px-4 py-2 rounded-xl cursor-not-allowed">Conectar con Facebook (próximamente)</button>
          </>
        ) : (
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px] text-seryi-text">
            <div><dt>Página</dt><dd className="text-seryi-heading font-bold">{conn.page_name || '—'}</dd></div>
            <div><dt>Cuenta publicitaria</dt><dd className="text-seryi-heading font-bold">{conn.ad_account_name || '—'}</dd></div>
            <div><dt>Última sincronización</dt><dd className="text-seryi-heading font-bold">{conn.last_sync_at ? ago(conn.last_sync_at) : 'todavía no'}</dd></div>
          </dl>
        )}
        {conn.last_error && <p role="alert" className="text-xs text-rose-300">{conn.last_error}</p>}
        {warning && <p role="alert" className="text-xs text-amber-300">{warning}</p>}
      </section>

      <AdCampaigns connected={conn.status === 'conectada'} limits={limits} />

      {month && (
        <section className={card} aria-label="Gasto del mes">
          <h3 className="text-sm font-black text-seryi-heading">Este mes</h3>
          <p className="text-xs text-seryi-heading">Llevas <strong className="text-base">{money(month.spend)}</strong> de un tope de {money(month.cap)} ({Math.round(month.percent)} %). El tope por día es {money(limits?.daily_cap)}.</p>
          <div className="w-full bg-seryi-bg-dark rounded-full h-2 overflow-hidden" role="progressbar" aria-label="Gasto del mes contra el tope" aria-valuenow={Math.min(100, Math.round(month.percent))} aria-valuemin={0} aria-valuemax={100}>
            <div className={`h-full rounded-full ${budgetTone(month.percent)}`} style={{ width: `${Math.min(100, Math.round(month.percent))}%` }} />
          </div>
          {month.percent > 100 && <p role="alert" className="text-xs text-rose-300">Ya pasaste el tope del mes: no se lanzarán anuncios nuevos hasta que el administrador lo ajuste.</p>}
          <p className="text-[10px] text-seryi-text">Los topes los define el administrador de Keyla para proteger tu tarjeta.</p>
        </section>
      )}

      {period && (
        <section className={card} aria-label="Últimos 30 días">
          <h3 className="text-sm font-black text-seryi-heading">Últimos 30 días</h3>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Fact label="Gastado" value={money(period.spend)} />
            <Fact label="Conversaciones" value={String(period.conversations)} />
            <Fact label="Costo por conversación" value={costText(period.cost_per_conversation)} note={goal.label} tone={goal.tone} />
            <Fact label="Clics" value={String(period.clicks)} />
          </dl>
          {campaigns.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] text-left">
                <caption className="sr-only">Campañas de los últimos 30 días</caption>
                <thead className="text-seryi-text"><tr><th className="py-1 pr-2">Campaña</th><th className="pr-2">Gastado</th><th className="pr-2">Conversaciones</th><th className="pr-2">Costo por conversación</th></tr></thead>
                <tbody className="text-seryi-heading">
                  {campaigns.map((c) => (
                    <tr key={c.ref} className="border-t border-seryi-bg-light">
                      <td className="py-1 pr-2">{c.name}</td><td className="pr-2">{money(c.spend)}</td><td className="pr-2">{c.conversations}</td><td className="pr-2">{costText(c.cost_per_conversation)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            !loading && <p className="text-xs text-seryi-text">Todavía no hay anuncios con cifras en este periodo.</p>
          )}
          <p className="text-[10px] text-seryi-text">El gasto de cada día se registra solo en Gastos (categoría «Marketing y publicidad») cuando el día lleva dos días cerrado, y entra al reporte semanal.</p>
        </section>
      )}
    </div>
  );
}
